import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Organizer } from '../../types/organizer';
import { useOrganizersStore } from '../../stores/organizersStore';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizer: Organizer;
};

export const ReplaceAuthModal = ({ isOpen, onClose, organizer }: Props) => {
  const { translations } = useLanguage();
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { replaceAuthCredentials } = useOrganizersStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error(translations?.replaceAuthModal?.allFieldsRequired || 'All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(translations?.replaceAuthModal?.passwordMismatch || 'Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error(translations?.replaceAuthModal?.passwordTooShort || 'Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      await replaceAuthCredentials(organizer.id, newEmail, newPassword);
      toast.success(translations?.replaceAuthModal?.success || 'Auth credentials replaced successfully');
      onClose();
    } catch (error: any) {
      console.error('Error replacing auth credentials:', error);
      toast.error(error.message || translations?.replaceAuthModal?.error || 'Failed to replace auth credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.replaceAuthModal?.title || `Replace Auth for ${organizer.name}`}
    >
      <div className="space-y-6">
        {/* Warning message */}
        <div className="bg-yellow-50 p-4 rounded-lg flex gap-3">
          <AlertCircle className="text-yellow-600 shrink-0 mt-1" size={20} />
          <div className="text-yellow-700 text-sm">
            <p className="font-medium mb-1">
              {translations?.replaceAuthModal?.warning?.title || 'Important Warning'}
            </p>
            <p>
              {translations?.replaceAuthModal?.warning?.message || 
                'This action will create a new authentication user with the provided credentials. The old authentication user will remain but will no longer be used. All related documents will be updated to reference the new user.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.replaceAuthModal?.newEmail || 'New Email'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              placeholder="example@domain.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.replaceAuthModal?.newPassword || 'New Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              required
              minLength={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.replaceAuthModal?.confirmPassword || 'Confirm Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              {translations?.common?.cancel || 'Cancel'}
            </button>
            
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {isLoading
                ? (translations?.common?.processing || 'Processing...')
                : (translations?.replaceAuthModal?.replace || 'Replace Credentials')}
            </motion.button>
          </div>
        </form>
      </div>
    </Modal>
  );
};